import { useState, useEffect, useRef, useCallback } from 'react';
import {
  apiService,
  type Conversation as ApiConversation,
  type Message as ApiMessage,
} from '../services/api';
import { parseStudioMarkdown, formatAnswerMarkdown } from '../utils/studioMarkdown';
import type { Message } from '../types/messages';
import logger from '../utils/simpleLogger';

/**
 * Normalize usage_stats from any format (nested from DB or flat from SSE) to flat format.
 * DB stores: {model, tokens: {prompt, completion, total}, costs_usd: {...}, costs_clp: {...}}
 * Frontend expects: {model_used, prompt_tokens, completion_tokens, total_tokens, cost_usd, cost_clp, ...}
 */
function normalizeUsageStats(
  raw: Record<string, any> | null | undefined
): Record<string, any> | undefined {
  if (!raw) return undefined;

  // Already flat format (from SSE emitter) — has prompt_tokens at top level
  if (typeof raw.prompt_tokens === 'number') return raw;

  // Nested format (from DB / calculate_cost) — needs flattening
  const tokens = raw.tokens || {};
  return {
    model_used: raw.model || raw.model_used || '',
    prompt_tokens: tokens.prompt ?? raw.prompt_tokens ?? 0,
    completion_tokens: tokens.completion ?? raw.completion_tokens ?? 0,
    total_tokens: tokens.total ?? raw.total_tokens ?? 0,
    cost_usd: typeof raw.costs_usd === 'object' ? raw.costs_usd?.total ?? 0 : raw.cost_usd ?? 0,
    cost_clp: typeof raw.costs_clp === 'object' ? raw.costs_clp?.total ?? 0 : raw.cost_clp ?? 0,
    word_count: raw.word_count ?? 0,
    elapsed_time_seconds: raw.elapsed_time_seconds ?? 0,
    cost_breakdown:
      typeof raw.costs_usd === 'object'
        ? {
            input_usd: raw.costs_usd?.input ?? 0,
            output_usd: raw.costs_usd?.output ?? 0,
            input_clp: raw.costs_clp?.input ?? 0,
            output_clp: raw.costs_clp?.output ?? 0,
          }
        : raw.cost_breakdown,
    timing_breakdown: raw.timing_breakdown,
  };
}

/**
 * Calculate RAG confidence from rag_stats.
 * Mirrors the calculation in useStreamingHandlers.ts finalizeMessage.
 */
function calculateConfidence(ragStats: Record<string, any> | null | undefined): number | undefined {
  if (!ragStats) return undefined;
  const totalChunks = ragStats.total_chunks_retrieved ?? 0;
  const relevantChunks = ragStats.relevant_chunks ?? totalChunks;
  const avgRelevance = ragStats.average_relevance ?? 0.5;
  const hasCitations = (ragStats.citations_count ?? 0) > 0;

  if (totalChunks === 0) {
    return 0.1;
  }
  const relevantRatio = relevantChunks / totalChunks;
  const relevanceFactor = avgRelevance * (0.5 + 0.5 * relevantRatio);
  const citationBoost = hasCitations ? 0.2 : 0;
  return Math.min(1.0, relevanceFactor + citationBoost);
}

export interface UseMessageManagementOptions {
  currentConversation: ApiConversation | null;
  conversations: ApiConversation[];
  selectConversation: (conversation: ApiConversation | null) => void;
  loadConversations: () => Promise<void>;
  createConversation: (title: string) => Promise<ApiConversation | null>;
  setError: (error: string | null) => void;
}

export interface UseMessageManagementReturn {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  loadConversationMessages: (conversationId: string) => Promise<void>;
  handleConversationCleared: () => Promise<void>;
  clearMessages: () => void;
  markConversationAsLoaded: (conversationId: string) => void;
}

/**
 * Hook para gestionar el estado y carga de mensajes de conversaciones
 *
 * Maneja:
 * - Estado de mensajes
 * - Carga de mensajes desde API
 * - Transformación de mensajes de API a formato local
 * - Preservación de mensajes de studio durante la carga
 * - Limpieza de conversación
 */
export function useMessageManagement({
  currentConversation,
  conversations,
  selectConversation,
  loadConversations,
  createConversation,
  setError,
}: UseMessageManagementOptions): UseMessageManagementReturn {
  const [messages, setMessages] = useState<Message[]>([]);

  const loadConversationMessages = useCallback(
    async (conversationId: string) => {
      logger.info('💬 [useMessageManagement] loadConversationMessages() called', {
        conversationId,
      });
      try {
        const response = await apiService.getConversation(conversationId);
        if (response.conversation && Array.isArray(response.conversation.messages)) {
          // Asegurar que currentConversation esté establecido cuando hay mensajes
          if (!currentConversation || currentConversation.id !== conversationId) {
            // Si tenemos la conversación de la respuesta, usarla directamente
            if (response.conversation) {
              selectConversation(response.conversation);
            } else {
              // Si no, recargar las conversaciones y buscar la correcta
              await loadConversations();
              const foundConversation = conversations.find((c) => c.id === conversationId);
              if (foundConversation) {
                selectConversation(foundConversation);
              }
            }
          }

          const formattedMessages = response.conversation.messages
            .filter((msg: ApiMessage | null | undefined) => !!msg && msg.role !== 'system')
            .map((msg: ApiMessage) => ({
              id: msg.id,
              text: msg.content ?? '',
              markdown: msg.content ?? '',
              isUser: msg.role === 'user',
              timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
              citations: msg.citations || {},
              excluded_from_context: msg.excluded_from_context || false,
              is_saved: msg.is_saved || false,
              usageStats: normalizeUsageStats(msg.usage_stats),
              ragStats: msg.rag_stats,
              confidence: msg.role !== 'user' ? calculateConfidence(msg.rag_stats) : undefined,
              aiFlowTrace: (msg as any).ai_flow_trace || (msg as any).aiFlowTrace, // Support both snake_case and camelCase
              selectedDocuments: msg.selected_documents || [],
              user_rating: msg.user_rating ?? null,
            }));

          logger.info('💬 [useMessageManagement] Messages retrieved', {
            conversationId,
            count: formattedMessages.length,
          });

          setMessages((prev: any) => {
            // Guard: if there are optimistic/streaming messages in progress,
            // don't overwrite them with API data (race condition during new conversation send)
            const hasActiveStreaming = prev.some(
              (m: any) =>
                m.id?.startsWith('temp-') || m.id?.startsWith('streaming-') || m.isStreaming
            );
            if (hasActiveStreaming) {
              logger.info(
                '💬 [useMessageManagement] Skipping message overwrite — active streaming'
              );
              return prev;
            }

            // Preservar mensajes de studio que no están en la API
            const studioMessages = prev
              .filter((m: any) => !!m.studioResult)
              .map((studioMsg: any) => {
                const savedContent = studioMsg.studioResult!;
                const markdownString =
                  typeof savedContent.content === 'string'
                    ? savedContent.content
                    : savedContent.content?.markdown ?? '';

                const rawResult =
                  savedContent.content &&
                  typeof savedContent.content === 'object' &&
                  'raw' in savedContent.content
                    ? savedContent.content.raw
                    : undefined;

                const metadata =
                  studioMsg.studioMetadata ||
                  (markdownString
                    ? (parseStudioMarkdown(markdownString).data as Record<string, any>)
                    : {});
                const usageStatsFromMetadata = metadata?.usage_stats;
                const ragStatsFromMetadata = metadata?.rag_stats;

                const answerMarkdown = formatAnswerMarkdown(
                  rawResult ?? savedContent.content,
                  undefined
                );
                const normalizedText =
                  answerMarkdown && answerMarkdown.trim().length > 0
                    ? answerMarkdown.trim()
                    : studioMsg.text && studioMsg.text.trim().length > 0
                      ? studioMsg.text.trim()
                      : '_Sin contenido disponible_';

                const stableId =
                  studioMsg.id && studioMsg.id !== 'studio-undefined'
                    ? studioMsg.id
                    : `studio-${savedContent.id ?? studioMsg.timestamp.getTime()}`;

                return {
                  ...studioMsg,
                  id: stableId,
                  text: normalizedText,
                  markdown: normalizedText,
                  studioMarkdown:
                    markdownString && markdownString.trim().length > 0 ? markdownString : undefined,
                  studioMetadata: metadata,
                  usageStats: normalizeUsageStats(usageStatsFromMetadata) || studioMsg.usageStats,
                  ragStats: ragStatsFromMetadata || studioMsg.ragStats,
                };
              });

            const studioWithoutDuplicates = studioMessages.filter(
              (studioMsg: any) =>
                !formattedMessages.some((apiMsg: any) => apiMsg.id === studioMsg.id)
            );
            const merged = [...formattedMessages, ...studioWithoutDuplicates];

            merged.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
            return merged;
          });
        } else {
          setMessages((prev) => prev.filter((m) => m.variant === 'studio-result'));
        }
      } catch (err) {
        console.error('Error loading messages:', err);
        logger.error('❌ [useMessageManagement] Error loading messages', {
          conversationId,
          error: err,
        });
      }
    },
    [currentConversation, conversations, selectConversation, loadConversations, setMessages]
  );

  const handleConversationCleared = useCallback(async () => {
    // Clear local messages immediately for UI responsiveness
    setMessages([]);

    // If no current conversation, nothing to delete
    if (!currentConversation) {
      return;
    }

    try {
      // Delete all messages from the conversation
      await apiService.deleteConversationMessages(currentConversation.id);
      logger.info('💬 [useMessageManagement] Messages deleted from conversation', {
        conversationId: currentConversation.id,
      });
      // Don't reload - we want it to stay empty
    } catch (err) {
      console.error('Error deleting messages:', err);
      setError('No se pudieron eliminar los mensajes');
      setTimeout(() => setError(null), 3000);
    }
  }, [currentConversation, setError]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Allow external code (e.g. conversation creation flow) to mark a conversation
  // as already loaded, preventing the auto-load effect from fetching (empty) messages
  // and wiping out optimistic/streaming messages.
  const markConversationAsLoaded = useCallback((conversationId: string) => {
    lastLoadedConversationIdRef.current = conversationId;
  }, []);

  // Cargar mensajes cuando se selecciona una conversación (incluyendo auto-selección)
  // Usar un ref para rastrear la última conversación cargada y evitar loops
  const lastLoadedConversationIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentConversation && currentConversation.id !== lastLoadedConversationIdRef.current) {
      // Solo cargar si es una conversación diferente a la última cargada
      logger.info('💬 [useMessageManagement] Auto-loading messages for conversation', {
        conversationId: currentConversation.id,
      });
      lastLoadedConversationIdRef.current = currentConversation.id;
      loadConversationMessages(currentConversation.id);
    }
    // loadConversationMessages is intentionally excluded: we only want to run when conversation ID changes,
    // not when the function reference changes. The lastLoadedConversationIdRef prevents infinite loops.
  }, [currentConversation?.id]);

  return {
    messages,
    setMessages,
    loadConversationMessages,
    handleConversationCleared,
    clearMessages,
    markConversationAsLoaded,
  };
}
